import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageSettings } from '../context/PageSettingsContext';
import './Navbar.css';

function Navbar() {
  const [scrolled,      setScrolled]      = useState(false);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [servicesOpen,  setServicesOpen]  = useState(false);
  const [productsOpen,  setProductsOpen]  = useState(false);
  const [companyOpen,   setCompanyOpen]   = useState(false);

  const location     = useLocation();
  const navigate     = useNavigate();
  const { user, role, signOut } = useAuth();
  const { isEnabled } = usePageSettings();

  const toggleRef = useRef(null);

  const dashboardPath = role === 'admin'
    ? '/admin'
    : role === 'internal_user' ? '/dashboard/internal' : '/dashboard/external';

  const handleAuthClick = async () => {
    if (user) { await signOut(); navigate('/login'); }
    else navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setServicesOpen(false);
    setProductsOpen(false);
    setCompanyOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close all menus on navigation
  useEffect(() => { closeMobileMenu(); }, [location]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => {
      if (toggleRef.current && !toggleRef.current.closest('nav').contains(e.target)) {
        closeMobileMenu();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileOpen]);

  const DropdownToggle = ({ label, isOpen, onToggle }) => (
    <span
      className="navbar-link dropdown-toggle"
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      aria-haspopup="true"
      onClick={onToggle}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      {label} ▾
    </span>
  );

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Main navigation">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" aria-label="BiltzenTech — Home">
          <img src="/logo.png" alt="BiltzenTech" className="logo-image" />
        </Link>

        <div className={`navbar-menu ${mobileOpen ? 'active' : ''}`}>
          {isEnabled('home') && <Link to="/" className="navbar-link">Home</Link>}

          {(isEnabled('services') || isEnabled('ai-solutions') || isEnabled('staffing')) && (
            <div
              className="navbar-dropdown"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <DropdownToggle label="Services" isOpen={servicesOpen} onToggle={() => setServicesOpen(o => !o)} />
              <div className={`dropdown-menu ${servicesOpen ? 'show' : ''}`} role="menu">
                {isEnabled('services')     && <Link to="/services"     className="dropdown-item" role="menuitem">All Services</Link>}
                {isEnabled('ai-solutions') && <Link to="/ai-solutions" className="dropdown-item" role="menuitem">AI Solutions</Link>}
                {isEnabled('staffing')     && <Link to="/staffing"     className="dropdown-item" role="menuitem">Staffing &amp; Teams</Link>}
              </div>
            </div>
          )}

          <div
            className="navbar-dropdown"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <DropdownToggle label="Our Products" isOpen={productsOpen} onToggle={() => setProductsOpen(o => !o)} />
            <div className={`dropdown-menu ${productsOpen ? 'show' : ''}`} role="menu">
              <Link to="/products/vision-platform" className="dropdown-item" role="menuitem">
                <span className="dropdown-item-icon" aria-hidden="true">👁️</span>
                <span>
                  <strong>Vision Intelligence Platform</strong>
                  <small>AI-powered manufacturing monitoring</small>
                </span>
              </Link>
            </div>
          </div>

          {isEnabled('case-studies') && <Link to="/case-studies" className="navbar-link">Case Studies</Link>}

          {(isEnabled('about') || isEnabled('industries') || isEnabled('blog')) && (
            <div
              className="navbar-dropdown"
              onMouseEnter={() => setCompanyOpen(true)}
              onMouseLeave={() => setCompanyOpen(false)}
            >
              <DropdownToggle label="Company" isOpen={companyOpen} onToggle={() => setCompanyOpen(o => !o)} />
              <div className={`dropdown-menu ${companyOpen ? 'show' : ''}`} role="menu">
                {isEnabled('about')      && <Link to="/about"      className="dropdown-item" role="menuitem">About Us</Link>}
                {isEnabled('industries') && <Link to="/industries"  className="dropdown-item" role="menuitem">Industries</Link>}
                {isEnabled('blog')       && <Link to="/blog"        className="dropdown-item" role="menuitem">Blog &amp; Insights</Link>}
              </div>
            </div>
          )}

          {isEnabled('careers') && <Link to="/careers" className="navbar-link">Careers</Link>}
          {isEnabled('contact') && <Link to="/contact" className="navbar-cta">Contact Us</Link>}

          {user ? (
            <div className="navbar-user">
              <Link to={dashboardPath} className="navbar-user-btn">
                <span aria-hidden="true">👤</span> {role?.replace('_', ' ')}
              </Link>
              <button className="navbar-signout-btn" onClick={handleAuthClick}>Sign Out</button>
            </div>
          ) : (
            <Link to="/login" className="navbar-login-btn">Login</Link>
          )}
        </div>

        <button
          ref={toggleRef}
          className="navbar-toggle"
          onClick={() => setMobileOpen(o => !o)}
          aria-expanded={mobileOpen}
          aria-controls="navbar-menu"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </button>
      </div>
    </nav>
  );
}

export default Navbar;
